/**
 * Fonte única para resolver o ano do ciclo comercial de um pedido e montar o `cycle_code`
 * enviado à API. Existia divergência entre fluxos (ano letivo do 1º item, `new Date().getFullYear()`,
 * etc.) que podia gerar `cycle_code` inconsistente com o ciclo real do carrinho.
 *
 * `ano_letivo` é um atributo do PRODUTO (edição/currículo), não identifica o ciclo comercial —
 * o mesmo produto (mesmo ano_letivo) pode ser vendido em ciclos comerciais diferentes (ex.:
 * 2026 e 2027). Por isso `selectedCycleYear` (o ciclo que o backend usou para servir o
 * catálogo/perfil atual) é sempre a fonte de verdade quando disponível; `ano_letivo` só serve
 * de fallback nos poucos fluxos legados em que nenhum ciclo foi explicitamente selecionado.
 */

export type OrderCycleType = "NORMAL" | "PROGRAMADO" | "SIMULADO";

const CYCLE_TYPE_SUFFIX: Record<OrderCycleType, string> = {
  NORMAL: "01",
  PROGRAMADO: "02",
  SIMULADO: "00",
};

/**
 * Rótulo de cada tipo de pedido pelo sufixo do `cycle_code`, no vocabulário que as telas já usam
 * (ver `StatusOrdersCompleted`). A API tem a mesma tabela em `OrderCycleCode.ToOrderTypeName`,
 * com os nomes dela ("Simulado"). "Semestral" (legado `PED-S`) não tem sufixo e não está aqui.
 */
export const CYCLE_TYPE_LABELS: Record<string, string> = {
  "01": "Anual",
  "02": "2º Semestre",
  "00": "Avaliações e Simulados",
};

/** Tipo de pedido de um `cycle_code` "AAAATT" (ex.: 202602 → "2º Semestre"). */
export function describeOrderTypeFromCycleCode(
  cycleCode: string | number | null | undefined,
): string | undefined {
  if (cycleCode == null || cycleCode === "") return undefined;
  const code = Number(cycleCode);
  if (!Number.isFinite(code)) return undefined;
  return CYCLE_TYPE_LABELS[String(Math.abs(code) % 100).padStart(2, "0")];
}

/**
 * Tipo de pedido considerando também o número do pedido. O fluxo NORMAL grava o pedido anual
 * (`PED-A-`) e o semestral (`PED-S-`) com o mesmo `cycle_code` ("AAAA01"), então só o sufixo
 * mostraria o semestral como "Anual". Mesma regra do filtro `cycleType` "01S" da API.
 */
export function describeOrderType(
  cycleCode: string | number | null | undefined,
  orderNumber: string | null | undefined,
): string | undefined {
  const byCode = describeOrderTypeFromCycleCode(cycleCode);
  return byCode === CYCLE_TYPE_LABELS["01"] && orderNumber?.startsWith("PED-S-") ? "Semestral" : byCode;
}

export function resolveOrderCycleYear(
  items: Array<{ ano_letivo?: number | string | null }>,
  selectedCycleYear?: number | null,
): number {
  if (selectedCycleYear) return selectedCycleYear;

  const anosLetivos = items
    .map((item) => Number(item.ano_letivo))
    .filter((ano) => Number.isFinite(ano) && ano > 0);

  if (anosLetivos.length > 0) return anosLetivos[0];

  return new Date().getFullYear();
}

export function buildCycleCode(year: number, type: OrderCycleType): string {
  return `${year}${CYCLE_TYPE_SUFFIX[type]}`;
}

/**
 * Data limite para ajustar itens de "2º Semestre" antes de eles serem processados
 * automaticamente como pedido Programado. Ver `index.tsx` (fluxo de finalização de
 * pedido NORMAL) para o uso na lógica de corte, e os componentes de UI que exibem
 * esse prazo ao cliente — todos devem derivar a data daqui, nunca hardcoded, para
 * acompanhar a virada de `orderYear`/`selectedCycleYear` de um ciclo para o outro.
 */
export function getSecondSemesterAdjustmentDeadline(year: number): Date {
  return new Date(`${year}-06-01T00:00:00`);
}

/**
 * Extrai o ano de um `cycle_code` (`${year}${suffix de 2 dígitos}`, ex.: "202701" → 2027).
 * Usado nas telas de detalhe de pedidos já criados, onde o ciclo correto para
 * listar produtos é o do próprio pedido — não o `selectedCycleYear` atual do usuário.
 */
export function resolveCycleYearFromCode(
  cycleCode: string | number | null | undefined,
): number | undefined {
  if (!cycleCode) return undefined;
  const year = Math.floor(Number(cycleCode) / 100);
  return Number.isFinite(year) && year > 0 ? year : undefined;
}

export type CommercialCycleStatus = "Draft" | "Published" | "Open" | "Closing" | "Closed";

export type CycleFlow = "order" | "view-only" | "order-readonly" | "unavailable";

/**
 * Decide qual fluxo mostrar para um ciclo a partir do seu status — `Open` permite
 * finalizar pedido, `Published` só permite visualização/simulação de preço (tela de
 * simulação), `Closing` abre a tela de pedido em modo somente leitura (consulta do ciclo
 * em fechamento, sem alterar nem finalizar pedido) e qualquer outro status não deve ser
 * oferecido ao cliente.
 */
export function resolveCycleFlow(status: CommercialCycleStatus | null | undefined): CycleFlow {
  if (status === "Open") return "order";
  if (status === "Published") return "view-only";
  if (status === "Closing") return "order-readonly";
  return "unavailable";
}
